import React, { useState, useEffect } from "react";
import { User, Mail, Phone, MapPin, Briefcase, Camera, Save, Edit } from "lucide-react";
import useConsultantAuthStore from "../../store/consultant/useConsultantAuthStore";

const ConsultantProfile = () => {
    const { consultant, updateProfile, getProfile } = useConsultantAuthStore();
    const [activeTab, setActiveTab] = useState("profile");
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        getProfile();
    }, []);

    useEffect(() => {
        if (!consultant) return;

        setFormData({
            name: consultant.name || consultant.engineerName || "",
            email: consultant.email || "",
            phone_number:
                consultant.phone_number ||
                consultant.contactNumber ||
                consultant.phone ||
                "",
            district: consultant.district || "",
            taluk: consultant.taluk || "",
            village_name: consultant.village_name || consultant.villageName || "",
            pincode: consultant.pincode || "",
            qualification: consultant.qualification || "",
            experience: consultant.experience || "",
            qualification_document: consultant.qualification_document || null,
            aadhaar_number:
                consultant.aadhaar_number ||
                consultant.aadhaarNumber ||
                consultant.aadhar_number ||
                consultant.aadharNumber ||
                consultant.aadhaar_no ||
                consultant.aadhaarNo ||
                consultant.aadhaar ||
                "",
        });
    }, [consultant]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const success = await updateProfile({ profileImage: file });
        if (success) {
            getProfile();
        }
    };

    const handleDocUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const success = await updateProfile({ qualificationDocument: file });
        if (success) {
            getProfile();
        }
    };

    const handleSubmit = async () => {
        const submitData = {
            ...formData,
            // Map frontend field name to backend column name
            aadhar_number: formData.aadhaar_number || undefined,
        };
        // Remove the frontend-only field to avoid duplicates
        delete submitData.aadhaar_number;

        const success = await updateProfile(submitData);
        if (success) {
            setIsEditing(false);
            getProfile();
        }
    };

    const displayImage = consultant?.profileImage || consultant?.profile_image || consultant?.engineerPhoto || "/assets/Support.png";

    const tabs = [
        { id: "profile", label: "Profile" },
        { id: "address", label: "Address" },
        { id: "documentation", label: "Documentation" },
    ];

    return (
        <div className="space-y-4">
            {/* Header Card */}
            <div className="bg-white shadow-sm rounded-xl text-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-5 sm:px-5">
                    <div className="flex flex-col sm:flex-row items-center sm:items-center gap-4 sm:gap-5 text-center sm:text-left">
                        <div className="relative">
                            <img
                                src={displayImage}
                                alt="Profile"
                                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-gray-200 shadow-sm object-cover bg-gray-50"
                                onError={(e) => { e.target.src = "/assets/Support.png"; }}
                            />
                            <label className="absolute -top-1 -right-1 bg-white rounded-full p-1.5 shadow-md cursor-pointer hover:bg-gray-100 border border-gray-100">
                                <Camera size={12} className="text-gray-600" />
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                            </label>
                        </div>

                        <div className="flex-1 w-full sm:w-auto">
                            <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">{formData.name || "Consultant"}</h2>
                            <p className="text-gray-500 text-[10px] sm:text-xs flex flex-wrap items-center justify-center sm:justify-start gap-1.5 sm:gap-2 mt-0.5">
                                <span className="truncate max-w-[150px] sm:max-w-none">{formData.email}</span>
                                <span className="text-gray-300 hidden sm:inline">•</span>
                                <span>{formData.district || "Location"}</span>
                            </p>
                            <div className="mt-3 flex flex-col items-center sm:items-start">
                                <div className="h-1.5 bg-gray-100 rounded-full w-32 sm:w-48 overflow-hidden">
                                    <div className="h-full bg-green-500 rounded-full" style={{ width: '96%' }}></div>
                                </div>
                                <p className="text-[10px] text-gray-400 mt-1 font-medium">96%</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Navigation */}
                <div className="bg-white border-t border-gray-50 px-2 pt-2">
                    <ul className="flex justify-start gap-2 text-gray-600 w-full px-2 text-sm overflow-x-auto no-scrollbar">
                        {tabs.map((tab) => (
                            <li
                                key={tab.id}
                                className={`cursor-pointer px-5 py-2.5 transition-all duration-200 select-none whitespace-nowrap text-[13px] font-medium ${activeTab === tab.id ? "bg-[#E0E0E0] text-gray-900 font-bold" : "hover:bg-gray-50 hover:text-gray-900"
                                    }`}
                                style={{ borderRadius: "10px 10px 0 0" }}
                                onClick={() => setActiveTab(tab.id)}
                            >
                                {tab.label}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Content Area */}
            <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
                <div className="p-5">
                    <div className="flex items-center gap-3 mb-6">
                        <h3 className="text-sm font-bold text-gray-900">
                            {activeTab === "profile" ? "My Details" : activeTab === "address" ? "Address Details" : "Documentation"}
                        </h3>
                        {isEditing ? (
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-[11px] font-bold hover:bg-blue-700 transition-all shadow-sm flex items-center gap-2"
                                >
                                    <Save size={14} />
                                    Save
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsEditing(false);
                                        getProfile();
                                    }}
                                    className="px-4 py-1.5 bg-gray-100 text-gray-600 rounded-md text-[11px] font-bold hover:bg-gray-200 transition-all font-sans"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setIsEditing(true)}
                                className="px-4 py-1.5 bg-blue-600 text-white rounded-md text-[11px] font-bold hover:bg-blue-700 transition-all shadow-sm flex items-center gap-2"
                            >
                                <Edit size={14} />
                                Edit
                            </button>
                        )}
                    </div>

                    {activeTab === "profile" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Full Name</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone Number</label>
                                <input
                                    type="tel"
                                    name="phone_number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Qualification</label>
                                <input
                                    type="text"
                                    name="qualification"
                                    value={formData.qualification}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Experience</label>
                                <input
                                    type="text"
                                    name="experience"
                                    value={formData.experience}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "address" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">District</label>
                                <input
                                    type="text"
                                    name="district"
                                    value={formData.district}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Taluk</label>
                                <input
                                    type="text"
                                    name="taluk"
                                    value={formData.taluk}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Village Name</label>
                                <input
                                    type="text"
                                    name="villageName"
                                    value={formData.village_name} // Keep using village_name from formData state
                                    onChange={(e) => handleInputChange({ target: { name: "village_name", value: e.target.value } })} // Map back to village_name
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Pincode</label>
                                <input
                                    type="text"
                                    name="pincode"
                                    value={formData.pincode}
                                    onChange={handleInputChange}
                                    disabled={!isEditing}
                                    className={`w-full px-3 py-2.5 rounded-md text-sm text-gray-700 outline-none transition-all border ${isEditing ? "bg-white border-gray-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-100" : "bg-gray-50/50 border-gray-100 disabled:opacity-80"
                                        }`}
                                />
                            </div>
                        </div>
                    )}

                    {activeTab === "documentation" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Aadhaar Number</label>
                                <input
                                    name="aadhaar_number"
                                    value={formData.aadhaar_number || ""}
                                    onChange={handleInputChange}
                                    disabled={true}
                                    maxLength={12}
                                    className="w-full px-3 py-2.5 rounded-md bg-gray-100 text-sm text-gray-500 cursor-not-allowed border-transparent outline-none border transition-all"
                                />
                                <p className="text-[10px] text-gray-400 italic">Aadhaar number cannot be modified after verification.</p>
                            </div>
                            <div className="space-y-1.5 flex flex-col justify-end">
                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Qualification Document</label>
                                <div className="flex items-center gap-3 bg-gray-50/50 p-2.5 rounded-md border border-gray-100">
                                    {formData.qualification_document ? (
                                        <a
                                            href={formData.qualification_document}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1.5"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                            </svg>
                                            View Document
                                        </a>
                                    ) : (
                                        <span className="text-gray-400 text-sm italic">No document uploaded</span>
                                    )}
                                    <label className="cursor-pointer bg-blue-50 text-blue-600 px-3 py-1 rounded-md text-[10px] font-bold hover:bg-blue-100 transition-colors border border-blue-100 ml-auto">
                                        Update
                                        <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={handleDocUpload} />
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </form>
        </div>
    );
};

export default ConsultantProfile;
